import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../molecules/PageHeader';
import TextEdit from '../../molecules/TextEdit';
import FileUpload from '../../molecules/FileUpload';
import ActionButton from '../../molecules/ActionButton';
import Loading from '../../ions/Loading';
import Dialog from '../../organisms/Dialog';
import { toast } from 'react-toastify';
import { uploadInBatches, BatchUploadProgress } from '../../../services/BatchUploadService';

const API_URL = process.env.REACT_APP_CONVERT_XML_API_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const OperationPageForm = () => {
    const navigate = useNavigate();
    const [operationName, setOperationName] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [summary, setSummary] = useState<any | null>(null);
    const [processingStatus, setProcessingStatus] = useState('');
    const [processedFiles, setProcessedFiles] = useState<any[]>([]);
    const [showSummary, setShowSummary] = useState(false);
    const [operationCode, setOperationCode] = useState('');
    const [uploadProgress, setUploadProgress] = useState<BatchUploadProgress | null>(null);

    const goToOperationsList = () => {
        navigate('/structuring/operations/0/12');
    };

    const handleSave = async () => {
        if (!operationName.trim()) {
            toast.error('Nome da operação é obrigatório');
            return;
        }

        if (files.length === 0) {
            toast.error('Selecione pelo menos um arquivo');
            return;
        }

        try {
            setIsProcessing(true);
            setUploadProgress(null);

            const result = await uploadInBatches(
                operationName,
                files,
                30, // Lotes de 30 arquivos
                (progress) => {
                    setUploadProgress(progress);
                    setProcessingStatus(`Processando lote ${progress.currentBatch} de ${progress.totalBatches}...`);
                }
            );

            if (result.success) {
                setSummary(result.summary);
                setOperationCode(result.operation_code);
                setShowSummary(true);
                toast.success('Operação processada com sucesso!');
            } else {
                setSummary(result.summary);
                setOperationCode(result.operation_code);
                setShowSummary(true);
                if (result.errors.length > 0) {
                    toast.error(`Upload parcialmente concluído. ${result.errors.length} lotes falharam.`);
                } else {
                    toast.error('Erro no processamento');
                }
            }
        } catch (err: any) {
            toast.error(err.message || 'Erro de conexão com o servidor');
        } finally {
            setIsProcessing(false);
            setUploadProgress(null);
        }
    };

    return (
        <div className="p-4 relative">
            {isProcessing && <Loading />}
            <PageHeader
                title="Nova operação"
                breadcrumb={[
                    { label: 'Estruturação', url: '/structuring/operations/0/12' },
                    { label: 'Nova operação', url: '#' },
                ]}
                apiVersion=""
            >
                <ActionButton
                    label={isProcessing ? 'Processando...' : 'Salvar'}
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleSave}
                />
            </PageHeader>
            <div className={`grid gap-4 mt-4 ${isProcessing ? 'pointer-events-none opacity-50' : ''}`}>
                <TextEdit
                    label="Nome da operação"
                    value={operationName}
                    onChange={(e) => setOperationName(e.target.value)}
                    disabled={isProcessing}
                />
                
                {uploadProgress && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                {processingStatus}
                            </span>
                            <span className="text-sm text-blue-600 dark:text-blue-400">
                                {uploadProgress.processedFiles}/{uploadProgress.totalFiles} arquivos
                            </span>
                        </div>
                        <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                            <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(uploadProgress.processedFiles / uploadProgress.totalFiles) * 100}%` }}
                            ></div>
                        </div>
                        <div className="flex justify-between text-xs text-blue-600 dark:text-blue-400 mt-1">
                            <span>Lote {uploadProgress.currentBatch}/{uploadProgress.totalBatches}</span>
                            <span>{Math.round((uploadProgress.processedFiles / uploadProgress.totalFiles) * 100)}%</span>
                        </div>
                    </div>
                )}
            </div>
            <div className="mt-4">
                <FileUpload
                    accept=".xml"
                    name="files"
                    label="Arquivos"
                    errorMessage=""
                    onUploadFile={(uploadedFile) => setFiles((prev) => [...prev, uploadedFile])}
                    onRemoveFile={() => setFiles([])}
                    disabled={isProcessing}
                    multiple={true}
                />
            </div>
            <Dialog
                id="summary-dialog"
                show={showSummary}
                onClose={() => {
                    setShowSummary(false);
                    setSummary(null);
                    setProcessingStatus('');
                    setProcessedFiles([]);
                    setOperationCode('');
                    // Clear form data only after showing success summary
                    setOperationName('');
                    setFiles([]);
                    goToOperationsList();
                }}
                header="Resumo do processamento"
                body={
                    <div className="p-4">
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="font-medium">Operação:</span>
                                <span>{operationCode}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Total de arquivos:</span>
                                <span>{summary?.total_files || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Uploads bem-sucedidos:</span>
                                <span className="text-green-600">{summary?.successful_uploads || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Uploads com falha:</span>
                                <span className="text-red-600">{summary?.failed_uploads || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Taxa de sucesso:</span>
                                <span>{summary?.success_rate || 0}%</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Status:</span>
                                <span>{processingStatus}</span>
                            </div>
                        </div>

                        {processedFiles && processedFiles.length > 0 && (
                            <div className="mt-6 border-t pt-4">
                                <h3 className="font-semibold mb-3">Arquivos processados:</h3>
                                <div className="max-h-40 overflow-y-auto">
                                    {processedFiles.map((file, index) => (
                                        <div key={index} className="text-sm py-1 border-b last:border-b-0">
                                            <div className="font-medium">{file.filename}</div>
                                            <div className="text-gray-500">{(file.size_bytes / 1024).toFixed(1)} KB</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                }
                footer={
                    <ActionButton
                        label="Fechar"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                            setShowSummary(false);
                            setSummary(null);
                            setProcessingStatus('');
                            setProcessedFiles([]);
                            setOperationCode('');
                            // Clear form data only after showing success summary
                            setOperationName('');
                            setFiles([]);
                            goToOperationsList();
                        }}
                    />
                }
            />
        </div>
    );
};

export default OperationPageForm;
